/**
 * Score Explanation Page
 * Full breakdown of how a score was calculated
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { getSnapshotExplanation, getSwingExplanation, type SnapshotScoringExplanation } from '@/lib/api/scoring';
import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from '@/components/golf/golf-card';
import { GolfButton } from '@/components/golf/golf-button';
import { BottomTabs } from '@/mvp/navigation/BottomTabs';

export default function ScoreExplanation() {
  const { id, type } = useParams<{ id: string; type?: string }>();
  const navigate = useNavigate();
  const [explanation, setExplanation] = useState<SnapshotScoringExplanation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadExplanation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Determine type from URL path
        const path = window.location.pathname;
        let data: SnapshotScoringExplanation;
        
        if (path.includes('/score-explanation/snapshot/')) {
          data = await getSnapshotExplanation(id);
        } else if (path.includes('/score-explanation/swing/')) {
          data = await getSwingExplanation(id);
        } else {
          // Try snapshot first, then swing
          try {
            data = await getSnapshotExplanation(id);
          } catch {
            data = await getSwingExplanation(id);
          }
        }
        
        setExplanation(data);
      } catch (err: any) {
        console.error('Error loading explanation:', err);
        setError(err.message || 'Failed to load explanation');
      } finally {
        setLoading(false);
      }
    };

    loadExplanation();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !explanation) {
    return (
      <div className="min-h-screen bg-background p-6">
        <GolfCard>
          <GolfCardContent className="text-center py-12">
            <p className="text-lg font-semibold text-foreground mb-2">
              {error || 'Explanation not found'}
            </p>
            <GolfButton onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </GolfButton>
          </GolfCardContent>
        </GolfCard>
        <BottomTabs />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-foreground">
              How Your Score Was Calculated
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Detailed breakdown of your swing quality score
            </p>
          </div>
        </div>

        {/* Score Display */}
        <GolfCard>
          <GolfCardContent>
            <div className="text-center">
              <div className="text-5xl font-bold text-primary mb-2">
                {explanation.final_score.toFixed(1)}
              </div>
              <p className="text-sm text-muted-foreground">
                {explanation.tier_label.replace(/_/g, ' ').toUpperCase()} • {explanation.peer_label.replace(/_/g, ' ')} for {explanation.skill_level} players
              </p>
            </div>
          </GolfCardContent>
        </GolfCard>

        {/* Peer Comparison */}
        <GolfCard>
          <GolfCardHeader>
            <GolfCardTitle>Peer Comparison</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Elite:</span>
                <span className={explanation.final_score >= explanation.peer_thresholds.elite ? 'font-semibold text-primary' : ''}>
                  {explanation.peer_thresholds.elite}+
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Strong:</span>
                <span className={explanation.final_score >= explanation.peer_thresholds.strong && explanation.final_score < explanation.peer_thresholds.elite ? 'font-semibold text-primary' : ''}>
                  {explanation.peer_thresholds.strong}-{explanation.peer_thresholds.elite - 1}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Solid:</span>
                <span className={explanation.final_score >= explanation.peer_thresholds.solid && explanation.final_score < explanation.peer_thresholds.strong ? 'font-semibold text-primary' : ''}>
                  {explanation.peer_thresholds.solid}-{explanation.peer_thresholds.strong - 1}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Weak:</span>
                <span className={explanation.final_score < explanation.peer_thresholds.solid ? 'font-semibold text-primary' : ''}>
                  &lt;{explanation.peer_thresholds.solid}
                </span>
              </div>
            </div>
          </GolfCardContent>
        </GolfCard>

        {/* Top 3 Faults Breakdown */}
        {explanation.top_3_faults.length > 0 && (
          <GolfCard>
            <GolfCardHeader>
              <GolfCardTitle>Top 3 Scoring Faults</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <div className="space-y-4">
                {explanation.top_3_faults.map((fault, idx) => (
                  <div key={idx} className="border-b border-border pb-4 last:border-0 last:pb-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">
                          {idx + 1}. {fault.taxonomy_key.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Found in {fault.consistency} of 5 swings
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-primary">
                          -{fault.fault_penalty.toFixed(1)} pts
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mt-2">
                      <div>Severity: {fault.median_severity}</div>
                      <div>Confidence: {(fault.avg_confidence * 100).toFixed(0)}%</div>
                      <div>Base Penalty: {fault.base_penalty}</div>
                      <div>Multiplier: {fault.confidence_multiplier}x</div>
                    </div>
                  </div>
                ))}
              </div>
            </GolfCardContent>
          </GolfCard>
        )}

        {/* Occasional Faults */}
        {explanation.occasional_faults.length > 0 && (
          <GolfCard>
            <GolfCardHeader>
              <GolfCardTitle>Other Faults (Not Scored)</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <p className="text-sm text-muted-foreground mb-3">
                These faults appear in your swings but don't affect your score. They're still shown for curriculum mapping.
              </p>
              <div className="space-y-2">
                {explanation.occasional_faults.map((fault, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{fault.taxonomy_key.replace(/_/g, ' ')}</span>
                    <span className="text-muted-foreground">
                      {fault.consistency}/5 swings
                    </span>
                  </div>
                ))}
              </div>
            </GolfCardContent>
          </GolfCard>
        )}

        {/* Calculation Steps */}
        <GolfCard>
          <GolfCardHeader>
            <GolfCardTitle>Calculation Steps</GolfCardTitle>
          </GolfCardHeader>
          <GolfCardContent>
            <div className="space-y-2">
              {explanation.calculation_steps.map((step, idx) => (
                <div key={idx} className="text-sm text-muted-foreground">
                  {idx + 1}. {step}
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Base Total Penalty:</span>
                <span className="font-semibold">{explanation.base_total_penalty.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Compounding Multiplier:</span>
                <span className="font-semibold">{explanation.compounding_multiplier.toFixed(2)}x</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Total Penalty:</span>
                <span className="font-semibold text-destructive">{explanation.total_penalty.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mt-2 pt-2 border-t border-border">
                <span className="font-medium">Final Score:</span>
                <span className="font-bold text-primary text-lg">{explanation.final_score.toFixed(1)}</span>
              </div>
            </div>
          </GolfCardContent>
        </GolfCard>

        {/* Strengths */}
        {explanation.strengths && explanation.strengths.length > 0 && (
          <GolfCard>
            <GolfCardHeader>
              <GolfCardTitle>Strengths</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <ul className="space-y-1">
                {explanation.strengths.map((strength, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground">
                    • {strength.replace(/_/g, ' ')}
                  </li>
                ))}
              </ul>
            </GolfCardContent>
          </GolfCard>
        )}

        {/* FAQ */}
        {explanation.faq && explanation.faq.length > 0 && (
          <GolfCard>
            <GolfCardHeader>
              <GolfCardTitle>Frequently Asked Questions</GolfCardTitle>
            </GolfCardHeader>
            <GolfCardContent>
              <div className="space-y-4">
                {explanation.faq.map((item, idx) => (
                  <div key={idx}>
                    <p className="font-medium text-sm mb-1">{item.question}</p>
                    <p className="text-sm text-muted-foreground">{item.answer}</p>
                  </div>
                ))}
              </div>
            </GolfCardContent>
          </GolfCard>
        )}
      </div>
      
      <BottomTabs />
    </div>
  );
}

